import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SEO from "@/components/app/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

const fmtEur = (cents) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format((cents || 0) / 100);
const fmtNum = (n) => new Intl.NumberFormat("fr-FR").format(n || 0);

export default function FinanceursPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/financeurs/data").then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        title="Dossier investisseurs"
        description="Résiliation Express — présentation confidentielle destinée aux investisseurs et partenaires financiers."
        noindex
      />

      <div className="max-w-5xl mx-auto px-4 py-16 space-y-14">
        <header className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">Dossier confidentiel — Q1 2026</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Résiliation Express</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Plateforme française d'accompagnement à la résiliation de contrats et abonnements — LegalTech · InsurTech · RegTech.</p>
        </header>

        {/* Problem */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Le problème</h2>
          <p className="text-muted-foreground leading-relaxed">
            En France, chaque foyer souscrit en moyenne à plus de 15 contrats et abonnements récurrents : assurances,
            mutuelles, télécoms, énergie, banque, streaming, salle de sport, presse, transport… Résilier reste
            <strong> lent, opaque et anxiogène</strong> : coordonnées difficiles à trouver, préavis mal compris, courriers
            recommandés à préparer soi-même, aucune trace centralisée.
          </p>
          <ul className="grid sm:grid-cols-2 gap-3 text-sm">
            {["Coordonnées de résiliation dispersées ou périmées", "Motifs légitimes et préavis complexes", "Absence de suivi et de preuves centralisées", "Aucun accompagnement dédié aux courtiers"].map((t) => (
              <li key={t} className="rounded-md border border-border p-3 bg-secondary/40">{t}</li>
            ))}
          </ul>
        </section>

        {/* Solution */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Notre solution</h2>
          <p className="text-muted-foreground leading-relaxed">
            Une plateforme SaaS complète qui guide le particulier de la recherche de l'organisme jusqu'à la preuve de
            distribution du recommandé, et qui offre aux courtiers un espace professionnel pour traiter leurs mandats en masse.
          </p>
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            {["Annuaire vérifié et administrable", "Générateur de courriers personnalisés", "Paiement PayPal Business (SCA)", "Envoi recommandé + suivi", "Coffre-fort de preuves (RGPD)", "Espace courtier B2B"].map((t) => (
              <li key={t} className="rounded-md border border-border p-3 bg-secondary/40 list-none">{t}</li>
            ))}
          </div>
        </section>

        {/* KPIs */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Indicateurs réels de la plateforme</h2>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</div>
          ) : !data ? (
            <p className="text-sm text-muted-foreground">Aucune donnée disponible pour cette période.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <Kpi label="Organismes référencés" value={fmtNum(data.kpis.organizations)} testid="kpi-orgs" />
              <Kpi label="Catégories" value={fmtNum(data.kpis.categories)} testid="kpi-cats" />
              <Kpi label="Guides publiés" value={fmtNum(data.kpis.guides)} testid="kpi-guides" />
              <Kpi label="Articles de blog" value={fmtNum(data.kpis.blog_articles)} testid="kpi-blog" />
              <Kpi label="Comptes créés" value={fmtNum(data.kpis.users)} testid="kpi-users" />
              <Kpi label="Dossiers ouverts" value={fmtNum(data.kpis.cases_started)} testid="kpi-cases" />
              <Kpi label="Lettres générées" value={fmtNum(data.kpis.letters_generated)} testid="kpi-letters" />
              <Kpi label="Paiements complétés" value={fmtNum(data.kpis.payments_completed)} testid="kpi-payments" />
              <Kpi label="Chiffre d'affaires" value={fmtEur(data.kpis.revenue_cents)} testid="kpi-revenue" highlight />
              <Kpi label="Panier moyen" value={fmtEur(data.kpis.avg_basket_cents)} testid="kpi-basket" />
              <Kpi label="Cabinets courtiers" value={fmtNum(data.kpis.broker_companies)} testid="kpi-brokers" />
            </div>
          )}
          <p className="text-xs text-muted-foreground">Chiffres mis à jour en temps réel depuis la base de production. Chiffres nuls tant que le trafic n'est pas engagé.</p>
        </section>

        {/* Business model */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Modèle économique</h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="rounded-md border border-border p-4 bg-secondary/40 space-y-2">
              <div className="font-semibold">B2C — particuliers</div>
              <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                <li>Téléchargement du courrier : gratuit (acquisition)</li>
                <li>Offre sur mesure : <strong className="text-foreground">50 % de la cotisation annuelle du contrat</strong></li>
                <li>Envoi assisté (recommandé + suivi + preuves) : <strong className="text-foreground">9,90 € TTC</strong></li>
                <li>Premium (vérification humaine, prioritaire) : <strong className="text-foreground">14,90 € TTC</strong></li>
              </ul>
            </div>
            <div className="rounded-md border border-border p-4 bg-secondary/40 space-y-2">
              <div className="font-semibold">B2B — courtiers et cabinets</div>
              <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                <li>Portefeuille de crédits PayPal, tarif dégressif</li>
                <li>Traitement en masse via import CSV / mandats</li>
                <li>Abonnement d'accès premium (roadmap)</li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Hypothèses : marge brute ~ 70 % sur B2C hors frais PayPal (2,9 %) et coûts postaux.</p>
        </section>

        {/* Budget */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Besoin de financement</h2>
          {data && (
            <>
              <div className="text-3xl font-bold" data-testid="fin-total">{fmtEur((data.budget.total_eur || 0) * 100)}</div>
              <div className="grid sm:grid-cols-2 gap-2 text-sm">
                {(data.budget.breakdown || []).map((row, i) => (
                  <div key={i} className="flex justify-between rounded-md border border-border p-3 bg-secondary/40">
                    <span className="text-muted-foreground">{row.label}</span>
                    <span className="font-semibold font-mono">{fmtEur((row.amount_eur || 0) * 100)}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">La répartition ci-dessus est administrable depuis l'espace administrateur.</p>
            </>
          )}
        </section>

        {/* Roadmap */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Feuille de route</h2>
          <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
            <li>Finalisation PayPal Business Live (webhook + réconciliation)</li>
            <li>Intégration postale Live (LaPoste ou équivalent) et automatisation</li>
            <li>Acquisition SEO et Google Ads, contenus de longue traîne (17+ guides déjà publiés)</li>
            <li>Partenariats courtiers (assurance emprunteur, mutuelles, énergie)</li>
            <li>Ouverture d'un poste support client + un poste développeur</li>
          </ol>
        </section>

        {/* Team plan */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Emplois à financer</h2>
          <p className="text-xs text-muted-foreground">Objectifs futurs — aucun de ces postes n'existe encore aujourd'hui.</p>
          <ul className="grid sm:grid-cols-2 gap-3 text-sm">
            {["Support client (front line)", "Commercial B2B courtiers", "Responsable contenu et données annuaire", "Développeur produit", "Responsable opérations postales"].map((r) => (
              <li key={r} className="rounded-md border border-border p-3 bg-secondary/40">{r}</li>
            ))}
          </ul>
        </section>

        <footer className="text-center text-xs text-muted-foreground pt-8 border-t border-border">
          Document confidentiel — destiné exclusivement aux investisseurs et partenaires financiers. Ne pas diffuser.
        </footer>
      </div>
    </>
  );
}

function Kpi({ label, value, testid, highlight }) {
  return (
    <Card data-testid={testid} className={highlight ? "border-primary" : ""}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-2xl font-bold mt-1 font-mono">{value}</div>
      </CardContent>
    </Card>
  );
}
